import { useState } from 'react';
import { Globe, FileText, Download, Volume2 } from 'lucide-react';
import { ImageLightbox } from '@reflex/ui/components/content/ImagePreview';
import { EventDetailTooltip } from '@reflex/ui/components/stream/EventMeta';
import type { ImageBlock, AudioBlock, EmbeddedResourceBlock, ResourceLinkBlock } from '../types.js';
import { CopyButton } from './shared.js';

const MIME_TO_LANG: Record<string, string> = {
  'application/json': 'json',
  'application/javascript': 'javascript',
  'text/javascript': 'javascript',
  'text/typescript': 'typescript',
  'text/html': 'html',
  'text/css': 'css',
  'text/markdown': 'markdown',
  'application/xml': 'xml',
  'text/xml': 'xml',
  'application/yaml': 'yaml',
  'text/yaml': 'yaml',
  'text/x-python': 'python',
  'text/x-rust': 'rust',
  'text/x-go': 'go',
  'text/x-java': 'java',
  'text/x-c': 'c',
  'text/x-cpp': 'cpp',
  'text/x-shell': 'bash',
};

export function ResourceLinkBlockView({ block }: { block: ResourceLinkBlock }) {
  const label = block.title ?? block.name ?? block.uri;
  return (
    <div className="group/resource-link relative">
      <a
        href={block.uri}
        target="_blank"
        rel="noopener noreferrer"
        className="my-0.5 flex items-center gap-1.5 rounded-md border border-border/50 bg-muted/30 px-2 py-1.5 text-xs text-status-info hover:text-foreground transition-colors"
      >
        <Globe className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
        {block.name && block.title && block.name !== block.title && (
          <span className="shrink-0 text-xs text-muted-foreground">{block.name}</span>
        )}
      </a>
      {block.sourceEvent && (
        <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 opacity-0 transition-opacity group-hover/resource-link:pointer-events-auto group-hover/resource-link:opacity-100">
          <EventDetailTooltip event={block.sourceEvent} />
        </span>
      )}
    </div>
  );
}

export function ImageBlockView({ block }: { block: ImageBlock }) {
  const [open, setOpen] = useState(false);
  const src = block.uri ?? `data:${block.mimeType};base64,${block.data}`;
  return (
    <div className="group/image relative my-1 inline-block">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="cursor-zoom-in rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="View full size"
      >
        <img
          src={src}
          alt="Agent output"
          className="max-h-64 rounded-md border border-border/50 block"
        />
      </button>
      {block.sourceEvent && (
        <span className="pointer-events-none absolute right-2 top-2 rounded-full bg-background/80 p-0.5 opacity-0 transition-opacity group-hover/image:pointer-events-auto group-hover/image:opacity-100">
          <EventDetailTooltip event={block.sourceEvent} />
        </span>
      )}
      {/* The shared viewer, so an agent screenshot behaves like every other
          image in the app: same chrome, and the same point-and-comment
          affordance when the host offers one. */}
      <ImageLightbox
        images={[{ src, alt: 'Agent output', originalUrl: block.uri ?? undefined }]}
        open={open}
        onOpenChange={setOpen}
      />
    </div>
  );
}

export function AudioBlockView({ block }: { block: AudioBlock }) {
  const src = `data:${block.mimeType};base64,${block.data}`;
  return (
    <div className="group/audio my-1 flex items-center gap-2 rounded-md border border-border/50 bg-muted/30 p-2">
      <Volume2 className="h-4 w-4 shrink-0 text-muted-foreground" />
      <audio controls src={src} className="h-8 flex-1" />
      {block.sourceEvent && (
        <span className="shrink-0 opacity-0 transition-opacity group-hover/audio:opacity-100">
          <EventDetailTooltip event={block.sourceEvent} />
        </span>
      )}
    </div>
  );
}

export function EmbeddedResourceBlockView({ block }: { block: EmbeddedResourceBlock }) {
  const basename = block.uri.split('/').pop() ?? block.uri;
  const lang = block.mimeType ? MIME_TO_LANG[block.mimeType] : undefined;

  if (block.text != null) {
    return (
      <div className="group/resource my-1 rounded-md border border-border/50 bg-muted/20 overflow-hidden">
        <div className="flex items-center gap-2 border-b border-border/50 bg-muted/30 px-3 py-1.5">
          <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate text-xs text-muted-foreground">{basename}</span>
          {block.sourceEvent && (
            <span className="opacity-0 transition-opacity group-hover/resource:opacity-100">
              <EventDetailTooltip event={block.sourceEvent} />
            </span>
          )}
          <CopyButton text={block.text} />
        </div>
        <pre
          className={`max-h-64 overflow-auto p-3 text-xs font-mono whitespace-pre-wrap leading-relaxed text-foreground/70${lang ? ` language-${lang}` : ''}`}
        >
          {block.text}
        </pre>
      </div>
    );
  }

  if (block.blob != null) {
    const href = `data:${block.mimeType ?? 'application/octet-stream'};base64,${block.blob}`;
    return (
      <div className="group/resource my-1 rounded-md border border-border/50 bg-muted/30 p-2.5">
        <div className="flex items-center gap-2 text-xs">
          <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate text-muted-foreground">{basename}</span>
          {block.sourceEvent && (
            <span className="opacity-0 transition-opacity group-hover/resource:opacity-100">
              <EventDetailTooltip event={block.sourceEvent} />
            </span>
          )}
          <a
            href={href}
            download={basename}
            className="flex items-center gap-1 text-status-info hover:text-foreground transition-colors"
          >
            <Download className="h-3 w-3" />
            Download
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="group/resource my-1 rounded-md border border-border/50 bg-muted/30 p-2.5">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <FileText className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{basename}</span>
        {block.sourceEvent && (
          <span className="ml-auto opacity-0 transition-opacity group-hover/resource:opacity-100">
            <EventDetailTooltip event={block.sourceEvent} />
          </span>
        )}
      </div>
    </div>
  );
}
